import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  SocialCommentHandlingStatusFilter,
  SocialCommentPlatformFilter,
} from "@multi-chat/shared";
import { syncSocialComments } from "../../../api/social-comments-api.js";
import { useInboxUnreadConversationCount } from "../InboxRealtimeProvider.js";
import type { CommentsViewMode } from "./comments-url-state.js";
import { useCommentsSummary } from "./use-comments-summary.js";
import { useCommentThread } from "./use-comment-thread.js";
import { useSocialCommentsList } from "./use-social-comments-list.js";
import { useSocialPostsList } from "./use-social-posts-list.js";

export function useCommentsWorkspace(
  organizationId: number,
  options: {
    active: boolean;
    viewMode: CommentsViewMode;
    commentId: string | null;
    postId: string | null;
  },
) {
  const { t } = useTranslation();
  const unreachable = t("inbox.unreachable");
  const messagesUnread = useInboxUnreadConversationCount();
  const { summary, refresh: refreshSummary } = useCommentsSummary(organizationId);
  const commentsEnabled = options.active && options.viewMode === "comments";
  const postsEnabled = options.active && options.viewMode === "posts";
  const threadEnabled = options.active;

  const [platform, setPlatform] = useState<SocialCommentPlatformFilter | undefined>();
  const [handlingStatus, setHandlingStatus] =
    useState<SocialCommentHandlingStatusFilter>("OPEN");
  const [sort, setSort] = useState<"activity_desc" | "activity_asc">("activity_desc");
  const [search, setSearch] = useState<string | undefined>();
  const [syncing, setSyncing] = useState(false);

  const commentsList = useSocialCommentsList(
    organizationId,
    {
      platform,
      handlingStatus,
      postId: options.postId ?? undefined,
      search,
      sort,
    },
    unreachable,
    commentsEnabled,
  );
  const postsList = useSocialPostsList(
    organizationId,
    {
      platform,
      search,
      hasOpenComments: true,
    },
    unreachable,
    postsEnabled,
  );
  const thread = useCommentThread(
    organizationId,
    options.commentId,
    unreachable,
    threadEnabled,
  );

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      await syncSocialComments(organizationId, platform ? { platform } : {});
      refreshSummary();
      commentsList.refresh();
      postsList.refresh();
      thread.refresh();
    } catch {
      /* list/thread error UI remains primary */
    } finally {
      setSyncing(false);
    }
  }, [commentsList, organizationId, platform, postsList, refreshSummary, thread]);

  return {
    messagesUnread,
    summary,
    platform,
    setPlatform,
    handlingStatus,
    setHandlingStatus,
    sort,
    setSort,
    search,
    setSearch,
    syncing,
    sync,
    commentsList,
    postsList,
    thread,
  };
}
